// Minimal HTML sanitizer: allow a safe subset of tags/attrs and strip the rest
export function sanitizeHtml(input: string): string {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${input}</div>`, "text/html");
    const allowedTags = new Set([
      "b",
      "strong",
      "i",
      "em",
      "u",
      "p",
      "br",
      "ul",
      "ol",
      "li",
      "span",
      "div",
      "a",
    ]);
    const walk = (node: Node) => {
      const element = node as HTMLElement;
      // Remove any script/style/comment nodes
      if (
        node.nodeType === Node.COMMENT_NODE ||
        (node.nodeType === Node.ELEMENT_NODE &&
          (element.tagName.toLowerCase() === "script" ||
            element.tagName.toLowerCase() === "style"))
      ) {
        node.parentNode?.removeChild(node);
        return;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = element.tagName.toLowerCase();
        if (!allowedTags.has(tag)) {
          // Replace disallowed element with its text content
          const text = doc.createTextNode(element.textContent || "");
          node.parentNode?.replaceChild(text, node);
          return;
        }
        // Strip all attributes except a safe subset
        const attributesToKeep: Record<string, Set<string>> = {
          a: new Set(["href", "title", "target", "rel"]),
          span: new Set(),
          div: new Set(),
          p: new Set(),
          b: new Set(),
          strong: new Set(),
          i: new Set(),
          em: new Set(),
          u: new Set(),
          ul: new Set(),
          ol: new Set(),
          li: new Set(),
          br: new Set(),
        };
        const keep = attributesToKeep[tag] ?? new Set<string>();
        // Copy attributes first to avoid live collection issues
        const attrs = Array.from(element.attributes);
        for (const attr of attrs) {
          const name = attr.name.toLowerCase();
          if (!keep.has(name) || name.startsWith("on")) {
            element.removeAttribute(attr.name);
            continue;
          }
          if (tag === "a" && name === "href") {
            const value = attr.value.trim();
            const isSafeLink = /^(https?:|mailto:|tel:)/i.test(value);
            if (!isSafeLink) {
              element.removeAttribute(attr.name);
            }
          }
        }
        if (tag === "a") {
          // Ensure safe defaults on links
          if (!element.getAttribute("target"))
            element.setAttribute("target", "_blank");
          const rel = element.getAttribute("rel") || "";
          const relParts = new Set(rel.split(" ").filter(Boolean));
          relParts.add("noopener");
          relParts.add("noreferrer");
          element.setAttribute("rel", Array.from(relParts).join(" "));
        }
      }
      // Walk children (snapshot first as we may mutate while iterating)
      const children = Array.from(node.childNodes);
      for (const child of children) {
        walk(child);
      }
    };
    const container = doc.body.firstElementChild as HTMLElement | null;
    if (!container) return "";
    walk(container);
    return container.innerHTML;
  } catch {
    return "";
  }
}
